import React, { useEffect, useState } from "react";
import Loader from "../components/Loader";
import { useTranslation } from "react-i18next";
import { Helmet } from "react-helmet";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import Newsletter from "../components/Newsletter";
import newsData from "../data/news.json";
import parse from "html-react-parser";
import NewsWidget from "../components/NewsWidget";
import GalleryMini from "../components/GalleryMini";
import BreadcrumbsTwo from "../components/BreadcrumbsTwo";
import StructuredData from "../seo/StructuredData";
import { breadcrumbJsonLd } from "../seo/jsonld";
import { DEFAULT_OG_IMAGE, SITE_LOGO, SITE_NAME, SITE_URL } from "../seo/site";

const findNewsByPath = (path) => {
  const newsArray = Object.values(newsData);
  const matchedNews =
    newsArray.find((news) => news.path === path || news.me_path === path) ||
    null;

  return matchedNews;
};

export default function News({ setHeader }) {
  const { slug } = useParams();
  const location = useLocation();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [currentBlog, setCurrentBlog] = useState(findNewsByPath(slug) || null);
  const pageLang = i18n?.language === "me" ? "me" : "en";
  const pageUrl = `${SITE_URL}${location.pathname}`;
  const pageTitle = currentBlog
    ? `${t(currentBlog.title)} - ${t("header.press.pageTitle")} - ${SITE_NAME}`
    : `${t("header.press.pageTitle")} - ${SITE_NAME}`;
  const pageDescription =
    (currentBlog?.desc && t(currentBlog.desc)) ||
    t("header.press.metaDescription");
  const ogImage = currentBlog?.img
    ? currentBlog.img.startsWith("http")
      ? currentBlog.img
      : `${SITE_URL}${currentBlog.img}`
    : DEFAULT_OG_IMAGE;
  const enUrl = currentBlog
    ? `${SITE_URL}/en/news/${currentBlog.path}`
    : `${SITE_URL}/en/news`;
  const meUrl = currentBlog
    ? `${SITE_URL}/me/vijesti/${currentBlog.me_path || currentBlog.path}`
    : `${SITE_URL}/me/vijesti`;
  const publishedDate = currentBlog?.date_en || currentBlog?.date;
  const publishedIso = publishedDate
    ? new Date(publishedDate).toISOString()
    : undefined;
  const breadcrumbItems = [
    { name: SITE_NAME, item: `${SITE_URL}/${pageLang}` },
    {
      name: t("header.press.pageTitle"),
      item: pageLang === "en" ? `${SITE_URL}/en/news` : `${SITE_URL}/me/vijesti`,
    },
    currentBlog
      ? { name: t(currentBlog.title), item: pageUrl }
      : null,
  ].filter(Boolean);
  const newsArticleJsonLd = currentBlog
    ? {
        "@context": "https://schema.org",
        "@type": "NewsArticle",
        headline: t(currentBlog.title),
        description: pageDescription,
        image: [ogImage],
        datePublished: publishedIso,
        dateModified: publishedIso,
        mainEntityOfPage: pageUrl,
        author: {
          "@type": "Organization",
          name: SITE_NAME,
        },
        publisher: {
          "@type": "Organization",
          name: SITE_NAME,
          logo: {
            "@type": "ImageObject",
            url: SITE_LOGO,
          },
        },
      }
    : null;
  const dateText =
    location.pathname.split("/")[1] === "me"
      ? currentBlog?.date_me || currentBlog?.date_en || currentBlog?.date
      : currentBlog?.date_en || currentBlog?.date_me || currentBlog?.date;

  const crumbs = [
    {
      title: t("header.press.pageTitle"),
      link: `/${location.pathname.split("/")[1]}/${
        location.pathname.split("/")[1] === "en" ? "news" : "vijesti"
      }`,
    },
    {
      title: t(currentBlog?.title),
    },
  ];

  useEffect(() => {
    const news = findNewsByPath(slug);
    if (!news) {
      navigate("/404");
      return;
    }
    setCurrentBlog(news);
  }, [slug, navigate]);

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 1) {
        setHeader(true);
      } else {
        setHeader(false);
      }
    };

    window.addEventListener("scroll", handleScroll);

    return () => window.removeEventListener("scroll", handleScroll);
  }, [setHeader]);

  return (
    <>
      <Helmet htmlAttributes={{ lang: pageLang === "me" ? "sr-ME" : "en" }}>
        {/* <!-- meta tag --> */}
        <meta charSet="utf-8" />
        <title>
          {pageTitle}
        </title>

        {/* <!-- Meta Tags--> */}
        <meta name="description" content={pageDescription} />
        <meta
          name="keywords"
          content="durō, duro, niche perfumery, niche parfimerija,  parfimerija, galerija"
        />
        <meta name="author" content="intellect.se" />
        <link rel="canonical" href={pageUrl} />
        <link rel="alternate" href={enUrl} hrefLang="en" />
        <link rel="alternate" href={meUrl} hrefLang="sr-ME" />
        <link rel="alternate" href={meUrl} hrefLang="x-default" />

        {/* <!-- responsive tag --> */}
        <meta http-equiv="x-ua-compatible" content="ie=edge" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />

        {/* <!-- Template Favicon & Icons --> */}
        <link
          rel="icon"
          href="../assets/images/favicon/favicon.ico"
          sizes="any"
        />
        <link
          rel="icon"
          href="../assets/images/favicon/icon.svg"
          type="image/svg+xml"
        />
        <link
          rel="apple-touch-icon"
          href="../assets/images/favicon/apple-touch-icon.png"
        />
        <link
          rel="manifest"
          href="../assets/images/favicon/manifest.webmanifest"
        />
        <link
          rel="shortcut icon"
          type="image/x-icon"
          href="../assets/images/fav.png"
        />

        {/* <!-- Facebook Metadata --> */}
        <meta property="og:type" content="article" />
        <meta property="og:image:height" content="1200" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDescription} />
        <meta property="og:url" content={pageUrl} />
        <meta property="og:image" content={ogImage} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={pageDescription} />
        <meta name="twitter:url" content={pageUrl} />
        <meta name="twitter:image" content={ogImage} />
      </Helmet>
      <Loader />
      {/* <!-- Main content Start --> */}
      <div className="main-content">
        {/* <!-- Breadcrumbs Start --> */}
        <div className="rs-breadcrumbs img3">
          <div className="container">
            <div className="breadcrumbs-inner">
              <h1 className="page-title">
                {t("home.news.main")}
                {/*<span className="watermark">{home.news.main}</span>*/}
              </h1>
            </div>
          </div>
        </div>
        <BreadcrumbsTwo bg="light" crumbs={crumbs} />
        {/* <!-- Breadcrumbs End --> */}

        {/* <!-- Blog Section Start --> */}
        <div className="rs-inner-blog gray-bg4 pt-120 md-pt-80">
          <div className="container">
            <div className="row">
              <div className="col-lg-8 md-mb-50">
                <div className="row">
                  <div className="col-lg-12 md-mb-50">
                    <div className="blog-details">
                      <div className="bs-img mb-35">
                        <a href="#!">
                          <img src={currentBlog?.img} alt="" />
                        </a>
                      </div>
                      <div className="blog-full">
                        <ul className="single-post-meta">
                          <li>
                            <span className="p-date">
                              <i className="fa fa-calendar-check-o"></i>{" "}
                              {dateText}{" "}
                            </span>
                          </li>
                          <li>
                            <span className="p-date">
                              {" "}
                              <i className="fa fa-user-o"></i>{" "}
                              {t(currentBlog?.admin)}{" "}
                            </span>
                          </li>
                          {/*<li className="Post-cate">
                            <div className="tag-line">
                              <i className="fa fa-book"></i>
                              <a href="#!">{t("news.strategy")}</a>
                            </div>
                          </li>
                          <li className="post-comment">
                            {" "}
                            <i className="fa fa-comments-o"></i> 1
                          </li>*/}
                        </ul>
                        {/*<p>{t("news.desc")}</p>
                        <blockquote>
                          <p className="margin-0">
                            {t("news.quote")}
                            <br />
                            <cite>Robert Calibo</cite>
                          </p>
                        </blockquote>*/}
                        <h3>{t(currentBlog?.title)}</h3>
                        <div>{parse(t(currentBlog?.content))}</div>
                        {currentBlog?.gallery?.length > 0 && (
                          <section className="mt-50" aria-label={t("locations.pageHeadings.gallery")}>
                            <h4>{t("locations.pageHeadings.gallery")}</h4>
                            <GalleryMini
                              key={currentBlog.path}
                              images={currentBlog.gallery}
                              imageAlt={t(currentBlog.title)}
                            />
                          </section>
                        )}
                        {/* <div className="bs-img mb-30">
                          <a href="#!">
                            <img src="../assets/images/blog/1.jpg" alt="" />
                          </a>
                        </div>
                        <p>{t("news.tips.desc2")}</p> */}

                        {/*<div className="tag-info">
                          Tags:
                          <a href="#!">{t("news.tips.tag1")}</a>
                          <a href="#!">{t("news.tips.tag2")}</a>
                          <a href="#!">{t("news.tips.tag3")}</a>
                          <a href="#!">{t("news.tips.tag4")}</a>
                      </div>*/}
                        {/* <h3 className="comment-title">
                          {t("news.comments.title")}
                        </h3>
                        <div className="comment-body">
                          <div className="comment-author">
                            <img
                              src="../assets/images/avatar.png"
                              alt="images"
                            />
                          </div>
                          <div className="comment-text">
                            <a href="#!">{t("news.comments.comment1.title")}</a>
                            <br />
                            <a className="timedate" href="#!">
                              <time dateTime="2021-05-26T08:43:43+00:00">
                                May 26, 2021 at 8:43 am
                              </time>
                            </a>
                            <p>{t("news.comments.comment1.desc")}</p>
                            <div className="reply-btn">
                              <a href="#!">{t("news.comments.reply")}</a>
                            </div>
                          </div>
                        </div>
                        <div className="comment-body">
                          <div className="comment-author">
                            <img
                              src="../assets/images/avatar.png"
                              alt="images"
                            />
                          </div>
                          <div className="comment-text">
                            <a href="#!">{t("news.comments.comment2.title")}</a>
                            <br />
                            <a className="timedate" href="#!">
                              <time dateTime="2021-05-26T08:43:43+00:00">
                                May 26, 2021 at 8:43 am
                              </time>
                            </a>
                            <p>{t("news.comments.comment2.desc")}</p>
                            <div className="reply-btn">
                              <a href="#!">{t("news.comments.reply")}</a>
                            </div>
                          </div>
                        </div>
                        <div className="comment-body">
                          <div className="comment-author">
                            <img
                              src="../assets/images/avatar.png"
                              alt="images"
                            />
                          </div>
                          <div className="comment-text">
                            <a href="#!">{t("news.comments.comment3.title")}</a>
                            <br />
                            <a className="timedate" href="#!">
                              <time dateTime="2021-05-26T08:43:43+00:00">
                                May 26, 2021 at 8:43 am
                              </time>
                            </a>
                            <p>{t("news.comments.comment3.title")}</p>
                            <div className="reply-btn">
                              <a href="#!">{t("news.comments.reply")}</a>
                            </div>
                          </div>
                        </div> */}
                        {/*<h3 className="comment-title">
                          {t("news.comments.leaveReply")}
                        </h3>
                        <p>{t("news.comments.note")}</p>

                        <div className="comment-note">
                          <div id="form-messages"></div>
                          <form
                            id="contact-form"
                            method="post"
                            action="mailer.php"
                          >
                            <fieldset>
                              <div className="row">
                                <div className="col-lg-6 mb-35 col-md-6 col-sm-6">
                                  <input
                                    className="from-control"
                                    type="text"
                                    id="name"
                                    name="name"
                                    placeholder="Name*"
                                    required=""
                                  />
                                </div>
                                <div className="col-lg-6 mb-35 col-md-6 col-sm-6">
                                  <input
                                    className="from-control"
                                    type="text"
                                    id="email"
                                    name="email"
                                    placeholder="E-Mail*"
                                    required=""
                                  />
                                </div>
                                <div className="col-lg-12 mb-30">
                                  <textarea
                                    className="from-control"
                                    id="message"
                                    name="message"
                                    placeholder="Your message Here"
                                    required=""
                                  ></textarea>
                                </div>
                              </div>
                              <div className="form-group mb-0">
                                <div className="post-btn">
                                  <input
                                    className="submit-post"
                                    type="submit"
                                    value="Post Comment"
                                  />
                                </div>
                              </div>
                            </fieldset>
                          </form>
                        </div>*/}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-lg-4 col-md-12 pl-35 md-pl-15">
                <div className="widget-area">
                  {/* <!--<div className="search-widget mb-50">
                                    <div className="search-wrap">
                                        <input type="search" placeholder="Searching..." name="s" className="search-input" value="">
                                        <button type="submit" value="Search"><i className="flaticon-search"></i></button>
                                    </div>
                                </div>--> */}
                  <NewsWidget />
                  {/* <!--<div className="categories mb-45">
                                    <div className="widget-title">
                                        <h3 className="title">{t("filters.categories", { defaultValue: "Categories" })}</h3>
                                    </div>
                                    <ul>
                                        <li><a href="#!">Branding</a></li>
                                        <li><a href="#!">Digital Marketing</a></li>
                                        <li><a href="#!">E-commerce</a></li>
                                        <li><a href="#!">Graphic Design</a></li>
                                    </ul>
                                </div>--> */}
                  <div className="follow-us">
                    <div className="widget-title mb-40">
                      <h3 className="title">{t("home.news.follow")}</h3>
                    </div>
                    <ul>
                      <li>
                        <a href="https://www.facebook.com/duro.montenegro">
                          <i className="fa fa-facebook"></i>
                        </a>
                      </li>
                      <li>
                        <a href="https://www.twitter.com/DuroCologne">
                          <i className="fa fa-twitter"></i>
                        </a>
                      </li>
                      <li>
                        <a href="https://www.instagram.com/duro_montenegro">
                          <i className="fa fa-instagram"></i>
                        </a>
                      </li>
                      <li>
                        <a href="https://www.youtube.com/@duro_montenegro">
                          <i className="fa fa-youtube"></i>
                        </a>
                      </li>
                      <li>
                        <a href="https://www.tiktok.com/@duro_montenegro">
                          <i className="fa-brands fa-tiktok"></i>
                        </a>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
          {/* <!-- Newsletter section start --> */}
          <div className="rs-newsletter style1 pt-120 md-pt-80 mb--65">
            <Newsletter />
          </div>
          {/* <!-- Newsletter section end --> */}
        </div>
        {/* <!-- Blog Section End -->  */}
      </div>
      {/* <!-- Main content End --> */}
      {newsArticleJsonLd ? (
        <StructuredData
          data={[
            breadcrumbJsonLd(breadcrumbItems),
            newsArticleJsonLd,
          ]}
        />
      ) : null}
    </>
  );
}
